import React from 'react';
import type { TaskTemplateListFilters, TemplateSourceFilter } from './templateManagementService';

interface TemplateFilterBarProps {
  filters: TaskTemplateListFilters;
  practiceAreas: Array<{ name: string; displayName: string }>;
  onChange: (next: TaskTemplateListFilters) => void;
}

const SOURCE_OPTIONS: Array<{ label: string; value: TemplateSourceFilter }> = [
  { label: 'All Sources', value: 'all' },
  { label: 'System', value: 'system' },
  { label: 'Firm Custom', value: 'custom' },
];

const KIND_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'All Types', value: '' },
  { label: 'Master (jurisdiction_base)', value: 'jurisdiction_base' },
  { label: 'Subtype Variant', value: 'jurisdiction_variant' },
  { label: 'Firm Variant', value: 'firm_variant' },
  { label: 'Base / Generic', value: 'base' },
];

const TemplateFilterBar: React.FC<TemplateFilterBarProps> = ({ filters, practiceAreas, onChange }) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Practice Area</span>
          <select
            value={filters.practice_area || ''}
            onChange={(event) => onChange({ ...filters, practice_area: event.target.value || undefined, page: 1 })}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">All Practice Areas</option>
            {practiceAreas.map((pa) => (
              <option key={pa.name} value={pa.name}>
                {pa.displayName}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Jurisdiction</span>
          <input
            value={filters.jurisdiction || ''}
            onChange={(event) => onChange({ ...filters, jurisdiction: event.target.value || undefined, page: 1 })}
            placeholder="State, county, or court"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Source</span>
          <select
            value={(filters.source as string) || 'all'}
            onChange={(event) =>
              onChange({
                ...filters,
                source: event.target.value as TemplateSourceFilter,
                page: 1,
              })
            }
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            {SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Template Type</span>
          <select
            value={(filters as any).template_kind || ''}
            onChange={(event) =>
              onChange({
                ...filters,
                ...(event.target.value ? { template_kind: event.target.value } : { template_kind: undefined }),
                page: 1,
              } as TaskTemplateListFilters)
            }
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            {KIND_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">Search</span>
          <input
            value={filters.search || ''}
            onChange={(event) => onChange({ ...filters, search: event.target.value || undefined, page: 1 })}
            placeholder="Search templates..."
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
      </div>
    </div>
  );
};

export default TemplateFilterBar;
